// 일일 패널 표시 모델(D3c): 장부 상태 → 줄 목록. DOM 없이 테스트한다.
import { DAILY_QUESTS } from '../shared/content/quests';
import {
  DUNGEON_INFO, DUNGEON_KINDS, TOWER_MAX, TOWER_UNLOCK_TOWN, dailyQuestGoal, isBossFloor, nextResetMs, questGold, type DailyState, type DungeonKind, type Reward,
} from '../shared/daily';
import { dungeonPerDay } from '../shared/content/liveops';

export interface QuestRowView { id: string; /** 목표 설명(현상금 등) */ hint?: string; name: string; progress: number; goal: number; claimed: boolean; claimable: boolean; locked: boolean; text: string; gold: number; stones: number; icon: 'sword' | 'anvil' | 'scroll' }
/** 일일 던전·탑 입장(GameSim enterDaily)이 연결됐는가. 지급 PR(L9-1)에서는 false — 장면 레인(L9-2)이 켠다. 꺼져 있으면 카드는 버튼이 아니다(눌러도 아무 일 없는 버튼 금지) */
export const DAILY_ENTRY_READY = true;

export interface DungeonRowView { kind: DungeonKind; name: string; left: number; perDay: number; locked: boolean; text: string; /** 해금되고 오늘 횟수가 남아 들어갈 수 있음 */ enterable: boolean }
export interface TowerView { best: number; next: number | null; /** 1~50 진행 띠 채움 0..1 */ ratio: number; boss: boolean; locked: boolean; startedToday: boolean; text: string; /** 오늘 도전 중(이어하기 가능) */ active: boolean; /** 해금되고 정복 전이며 오늘 도전 가능 또는 도전 중 */ enterable: boolean; /** 상태 알약 글자 */ stateLabel: string }

export function questRows(s: DailyState, town: number): QuestRowView[] {
  return DAILY_QUESTS.map((q) => {
    const g = dailyQuestGoal(q);
    const row = s.quests[q.id];
    const progress = row ? row.progress : 0;
    const claimed = row ? row.claimed : false;
    const locked = town < q.town;
    const claimable = !locked && !claimed && progress >= g.count;
    const text = locked ? '마을' + q.town + ' 해금' : claimed ? '수령 완료' : progress + ' / ' + g.count;
    const icon = q.id === 'daily-enhance' ? 'anvil' : q.id === 'daily-bounty' ? 'scroll' : 'sword';
    const hint = g.type === 'bounty' ? '퀘스트 창 G → 모험 탭에서 오늘의 현상금 확인' : undefined;
    const label = g.type === 'bounty' ? '현상금 보스 처치 ' + progress + ' / ' + g.count : text;
    return { id: q.id, hint, name: q.name, progress, goal: g.count, claimed, claimable, locked, text: locked || claimed ? text : label, gold: questGold(q, town), stones: q.rewards.stones ?? 0, icon };
  });
}

export function dungeonRows(s: DailyState, town: number): DungeonRowView[] {
  return DUNGEON_KINDS.map((kind) => {
    const info = DUNGEON_INFO[kind];
    const perDay = dungeonPerDay(kind, s.day);
    const left = Math.max(0, perDay - s.dungeon[kind]);
    const locked = town < info.town;
    return { kind, name: info.name, left, perDay, locked, text: locked ? '마을' + info.town + ' 해금' : '남은 ' + left + ' / ' + perDay, enterable: DAILY_ENTRY_READY && !locked && left > 0 };
  });
}

export function towerView(s: DailyState, town: number): TowerView {
  const locked = town < TOWER_UNLOCK_TOWN;
  const done = s.tower.best >= TOWER_MAX;
  const next = done ? null : s.tower.best + 1;
  const startedToday = s.tower.startedDay === s.day;
  const text = locked ? '마을' + TOWER_UNLOCK_TOWN + ' 해금' : done ? '1~' + TOWER_MAX + '층 정복' : '최고 ' + s.tower.best + '층 · 다음 ' + next + '층' + (startedToday ? ' · 오늘 도전함' : ' · 오늘 도전 가능');
  const active = startedToday && s.tower.active;
  const enterable = DAILY_ENTRY_READY && !locked && !done && (!startedToday || active);
  const stateLabel = done ? '정복' : active ? '이어서 ' + (s.tower.floor + 1) + '층' : startedToday ? '도전함' : '도전 가능';
  return { best: s.tower.best, next, ratio: s.tower.best / TOWER_MAX, boss: next !== null && isBossFloor(next), locked, startedToday, text, active, enterable, stateLabel };
}

export function rewardText(r: Reward): string {
  const parts: string[] = [];
  if (r.gold) parts.push('골드 ' + r.gold);
  if (r.stones) parts.push('강화석 ' + r.stones);
  if (r.gems) parts.push('젬 ' + r.gems);
  if (r.protect) parts.push('보호 주문서 ' + r.protect);
  return parts.length ? parts.join(' · ') : '없음';
}

/** 다음 초기화까지 "H시간 M분" (KST 00:00) */
export function resetText(nowMs: number): string {
  const left = Math.max(0, nextResetMs(nowMs) - nowMs);
  const h = Math.floor(left / 3600000), m = Math.floor((left % 3600000) / 60000);
  return h > 0 ? h + '시간 ' + m + '분' : m + '분';
}

/** 일일 판 결과 화면 문구(L9, PR #273 Codex P2): 일반 클리어·처치·카드 골드를 다시 계산하지 않고 실제 지급(result.gold·daily*)만 쓴다 */
export interface DailyResultLike {
  readonly cleared: boolean; readonly gold: number; readonly dailyKind?: string; readonly dailyFloor?: number;
  readonly dailyStones?: number; readonly dailyProtect?: number; readonly dailyGems?: number;
}
export function dailyResultLines(r: DailyResultLike): { title: string; reward: string } | null {
  const k = r.dailyKind;
  if (k !== 'vault' && k !== 'mine' && k !== 'tower') return null;
  const name = k === 'tower' ? '무한의 탑 ' + (r.dailyFloor ?? 0) + '층' : DUNGEON_INFO[k].name;
  if (!r.cleared) return { title: name + ' 실패', reward: k === 'tower' ? '오늘 탑 도전이 끝났어요 · 내일 이어서 오를 수 있어요' : '보상 없음 · 오늘 시도 1회를 썼어요' };
  const parts: string[] = [];
  if (r.gold > 0) parts.push(r.gold.toLocaleString('ko-KR') + ' 골드');
  if ((r.dailyStones ?? 0) > 0) parts.push('강화석 ' + r.dailyStones + '개');
  if ((r.dailyProtect ?? 0) > 0) parts.push('보호 주문서 ' + r.dailyProtect + '장');
  if ((r.dailyGems ?? 0) > 0) parts.push('젬 ' + r.dailyGems + '개(보관 — 서버 지급 연결 뒤)');
  return { title: k === 'tower' ? name + ' 돌파' : name + ' 클리어', reward: parts.length ? '보상 내역 · ' + parts.join(' · ') : '보상 없음' };
}
